import { InlineError } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useDebounced } from "@/hooks/useDebounced";
import {
  api,
  ApiError,
  type ChannelPlatform,
  type ChannelPlatformStatus,
} from "@/lib/api";
import { cn } from "@/lib/utils";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Hash, Lock, Plus, RefreshCw, Search, UserRound } from "lucide-react";
import { RadioGroup as RadioGroupPrimitive } from "radix-ui";
import { useState, type ReactNode } from "react";
import { PLATFORM_LABEL } from "./channel-mode";
import {
  bareChannelName,
  choiceKey,
  retryWhileRestarting,
  slackChannelName,
  type DefaultChannelChoice,
} from "@/lib/default-channel";

type Row = { channelOrSpaceId: string; displayName: string | null; surfaceType: string };

/**
 * One list: "no channel" first (wording from the caller), then the platform's
 * channels. Search filters; where the Boo can create channels, a search with
 * no exact match offers "Create #name", which stages a selected "New" row
 * (the search stays, as when selecting any other row):
 * the channel is only created when the caller saves (saveDefaultChannelChoice).
 * Refresh makes the Boo walk the platform again; the old list stays until the
 * new one lands. Platforms without a directory fall back to a channel ID.
 */
export function DefaultChannelPicker({
  containerId,
  platforms,
  value,
  onChange,
  noneOption,
  botName = "Boo",
  disabled = false,
  connecting = false,
}: {
  containerId: string;
  /** Eligible platforms (see defaultChannelPlatforms); may be empty. */
  platforms: ChannelPlatformStatus[];
  value: DefaultChannelChoice;
  onChange: (choice: DefaultChannelChoice) => void;
  noneOption: { title: string; detail?: string };
  /** For the private-channel /invite hint. */
  botName?: string;
  disabled?: boolean;
  /** The platform is still coming up (Boo restarting after a connect). */
  connecting?: boolean;
}) {
  const queryClient = useQueryClient();
  const [platform, setPlatform] = useState<ChannelPlatform>(
    value.kind !== "none" && platforms.some((p) => p.platform === value.platform)
      ? value.platform
      : (platforms[0]?.platform ?? "slack"),
  );
  const [searchInput, setSearchInput] = useState("");
  const q = useDebounced(searchInput.trim(), 200);
  const status = platforms.find((p) => p.platform === platform);
  const hasDirectory = status?.directorySupported === true;
  const canCreate = status?.createsChannels === true;

  const dirKey = ["channel-directory", containerId, platform, q] as const;
  const dir = useQuery({
    queryKey: dirKey,
    queryFn: () => api.containers.channels.directory(containerId, platform, q || undefined),
    enabled: !!status && hasDirectory,
    placeholderData: keepPreviousData,
    retry: retryWhileRestarting,
    retryDelay: 3000,
    // A cold brain cache answers at once with the rows walked so far; keep
    // asking until the walk is done.
    refetchInterval: (query) => (query.state.data?.progress?.done === false ? 1000 : false),
  });
  const progress = dir.data?.progress;
  const loadingMore = progress?.done === false;
  const usePicker = !!status && hasDirectory && dir.data?.supported !== false;
  const restarting = dir.error instanceof ApiError && dir.error.status === 503;

  const refresh = useMutation({
    mutationFn: () =>
      api.containers.channels.directory(containerId, platform, q || undefined, { refresh: true }),
    onSuccess: (data) => {
      queryClient.setQueryData(dirKey, data);
      void queryClient.invalidateQueries({
        queryKey: ["channel-directory", containerId, platform],
        predicate: (query) => query.queryKey[3] !== q,
      });
    },
  });

  // The channel selected on open (the current default) sits first; clicking
  // other rows never reorders. A selected channel the listing
  // lacks stays visible so the selection never points at nothing.
  const [pinnedKey] = useState<string | null>(() =>
    value.kind === "channel" ? choiceKey(value) : null,
  );
  const keyOf = (row: { channelOrSpaceId: string }) => `${platform}:${row.channelOrSpaceId}`;
  const channels = (dir.data?.channels ?? []).filter(
    (row) => row.surfaceType !== "dm" && row.surfaceType !== "group_dm",
  );
  const selectedRow: Row | undefined =
    value.kind === "channel" && value.platform === platform
      ? {
          channelOrSpaceId: value.channelOrSpaceId,
          displayName: value.displayName,
          surfaceType: value.surfaceType,
        }
      : undefined;
  const pinned: Row[] = [];
  if (q.length === 0) {
    const pinnedRow = pinnedKey
      ? (channels.find((row) => keyOf(row) === pinnedKey) ??
        (selectedRow && keyOf(selectedRow) === pinnedKey ? selectedRow : undefined))
      : undefined;
    if (pinnedRow) pinned.push(pinnedRow);
    if (
      selectedRow &&
      !pinned.some((row) => keyOf(row) === keyOf(selectedRow)) &&
      !channels.some((row) => keyOf(row) === keyOf(selectedRow))
    ) {
      pinned.push(selectedRow);
    }
  }
  const pinnedKeys = new Set(pinned.map(keyOf));
  const rows = [...pinned, ...channels.filter((row) => !pinnedKeys.has(keyOf(row)))];

  const selectRow = (row: Row) =>
    onChange({
      kind: "channel",
      platform,
      channelOrSpaceId: row.channelOrSpaceId,
      displayName: row.displayName,
      surfaceType: row.surfaceType,
    });

  // Search-to-create stages a draft; nothing is created until the caller saves.
  const [pendingPrivate, setPendingPrivate] = useState(false);
  const [draft, setDraft] = useState<{ name: string; isPrivate: boolean } | null>(() =>
    value.kind === "new" ? { name: value.name, isPrivate: value.isPrivate } : null,
  );
  const draftKey = draft ? `new:${platform}:${draft.name}` : null;
  const newName = platform === "slack" ? slackChannelName(searchInput) : searchInput.trim();
  const exists = channels.some((row) => bareChannelName(row.displayName) === newName);
  const offerCreate =
    canCreate && usePicker && newName.length > 0 && !exists && newName !== draft?.name;
  // Once the caller has created the draft (a save that failed after creating
  // it), the real channel is selected and the draft row would duplicate it.
  const draftCreated =
    draft !== null && value.kind === "channel" && bareChannelName(value.displayName) === draft.name;
  // Compare in Slack's form so "Pip Reports" still shows the staged pip-reports.
  const qName = platform === "slack" ? slackChannelName(q) : q.toLowerCase();
  const showDraft =
    draft !== null && !draftCreated && (q.length === 0 || draft.name.includes(qName));

  const stageDraft = () => {
    const next = { name: newName, isPrivate: pendingPrivate };
    setDraft(next);
    onChange({ kind: "new", platform, ...next });
    setPendingPrivate(false);
  };
  const setDraftPrivate = (isPrivate: boolean) => {
    if (!draft) return;
    const next = { ...draft, isPrivate };
    setDraft(next);
    if (value.kind === "new") onChange({ kind: "new", platform, ...next });
  };

  const radioValue = choiceKey(value);
  const onRadioChange = (key: string) => {
    if (key === "none") return onChange({ kind: "none" });
    if (draft && key === draftKey) return onChange({ kind: "new", platform, ...draft });
    const row = rows.find((r) => keyOf(r) === key);
    if (row) selectRow(row);
  };
  const warnings = dir.data?.warnings ?? [];
  const loadingRows = connecting || (usePicker && (dir.isLoading || restarting));

  return (
    <div className="flex flex-col gap-3">
      {platforms.length > 1 && (
        <div className="flex gap-1">
          {platforms.map((p) => (
            <Button
              key={p.platform}
              type="button"
              variant={platform === p.platform ? "secondary" : "ghost"}
              size="sm"
              onClick={() => {
                setPlatform(p.platform);
                setSearchInput("");
              }}
            >
              {PLATFORM_LABEL[p.platform]}
            </Button>
          ))}
        </div>
      )}

      {usePicker && (
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder={canCreate ? "Search or create a channel" : "Search channels"}
              value={searchInput}
              disabled={disabled}
              aria-label="Search channels"
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && offerCreate) {
                  e.preventDefault();
                  stageDraft();
                }
              }}
            />
          </div>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="shrink-0"
            aria-label="Refresh channels"
            title="Refresh channels"
            disabled={refresh.isPending || disabled}
            onClick={() => refresh.mutate()}
          >
            <RefreshCw className={cn("size-4", refresh.isPending && "animate-spin")} />
          </Button>
        </div>
      )}

      <RadioGroupPrimitive.Root
        value={radioValue}
        onValueChange={onRadioChange}
        disabled={disabled}
        aria-label="Where to check in"
        className={cn(
          // Fixed height so filtering never resizes the dialog or page.
          "flex h-72 flex-col gap-0.5 overflow-y-auto rounded-xl bg-background p-1",
          "shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.04)] dark:shadow-[0_0_0_1px_rgba(255,255,255,0.1)]",
          "transition-opacity duration-150",
          (dir.isPlaceholderData || refresh.isPending) && "opacity-60",
        )}
      >
        {q.length === 0 && searchInput.trim().length === 0 && (
          <PickerRow
            value="none"
            icon={<UserRound className="size-4" />}
            label={noneOption.title}
            detail={noneOption.detail}
          />
        )}

        {showDraft && draft && draftKey && (
          <div className="flex h-10 shrink-0 items-center gap-1 rounded-lg pr-1 transition-colors hover:bg-muted/60 has-[[data-state=checked]]:bg-muted">
            <RadioGroupPrimitive.Item
              value={draftKey}
              className="group flex h-full min-w-0 flex-1 items-center gap-3 rounded-lg px-3 text-left text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:opacity-60"
            >
              <span className="shrink-0 text-muted-foreground group-data-[state=checked]:text-foreground">
                {draft.isPrivate ? <Lock className="size-4" aria-label="Private" /> : <Hash className="size-4" />}
              </span>
              <span className="min-w-0 truncate">{draft.name}</span>
              <span className="shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground shadow-[0_0_0_1px_rgba(0,0,0,0.08)] dark:shadow-[0_0_0_1px_rgba(255,255,255,0.12)]">
                New
              </span>
              <span className="flex-1" />
              <SelectedCheck />
            </RadioGroupPrimitive.Item>
            <VisibilitySwitch
              isPrivate={draft.isPrivate}
              onChange={setDraftPrivate}
              disabled={disabled}
            />
          </div>
        )}

        {offerCreate && (
          <div className="flex h-10 shrink-0 items-center gap-1 rounded-lg pr-1 transition-colors hover:bg-muted/60">
            <button
              type="button"
              disabled={disabled}
              onClick={stageDraft}
              className="flex h-full min-w-0 flex-1 items-center gap-3 rounded-lg px-3 text-left text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            >
              <Plus className="size-4 shrink-0 text-muted-foreground" />
              <span className="truncate">
                Create{" "}
                <span className="font-medium">
                  {platform === "slack" ? `#${newName}` : newName}
                </span>
              </span>
            </button>
            <VisibilitySwitch
              isPrivate={pendingPrivate}
              onChange={setPendingPrivate}
              disabled={disabled}
            />
          </div>
        )}

        {loadingRows ? (
          <>
            {[62, 48, 70, 55].map((width) => (
              <div key={width} className="flex h-10 shrink-0 items-center gap-3 px-3">
                <Skeleton className="size-4 rounded" />
                <Skeleton className="h-3 rounded" style={{ width: `${width}%` }} />
              </div>
            ))}
            {(restarting || connecting) && (
              <p className="px-3 pt-1 text-xs text-muted-foreground">
                Connecting to {PLATFORM_LABEL[platform]}…
              </p>
            )}
          </>
        ) : usePicker && dir.isError ? (
          <div className="p-2">
            <InlineError error={dir.error} />
          </div>
        ) : (
          rows.map((row) => (
            <PickerRow
              key={keyOf(row)}
              value={keyOf(row)}
              icon={
                row.surfaceType === "private_channel" ? (
                  <Lock className="size-4" aria-label="Private" />
                ) : (
                  <Hash className="size-4" />
                )
              }
              label={bareChannelName(row.displayName) ?? row.channelOrSpaceId}
            />
          ))
        )}

        {usePicker && !loadingRows && loadingMore && (
          <p className="px-3 py-2 text-xs text-muted-foreground">
            Loading channels… {progress.channels.toLocaleString()} so far
          </p>
        )}

        {usePicker && dir.isSuccess && !loadingMore && q.length > 0 && rows.length === 0 && !showDraft && (
          <p className="px-3 py-2 text-xs text-muted-foreground">
            {platform !== "slack"
              ? "No match."
              : offerCreate
                ? `Looking for a private channel? Invite @${botName} to it first.`
                : `No match. For a private channel, invite @${botName} to it first.`}
          </p>
        )}

        {status && !hasDirectory && (
          <ChannelIdEntry
            containerId={containerId}
            platform={platform}
            disabled={disabled}
            selected={value.kind === "channel" && value.platform === platform ? value : null}
            onUse={selectRow}
          />
        )}
      </RadioGroupPrimitive.Root>

      {refresh.isError && <InlineError error={refresh.error} />}
      {warnings.map((warning) => (
        <p key={warning} className="text-xs text-amber-600 dark:text-amber-500">
          {warning}
        </p>
      ))}
    </div>
  );
}

function PickerRow({
  value,
  icon,
  label,
  detail,
}: {
  value: string;
  icon: ReactNode;
  label: string;
  detail?: string;
}) {
  return (
    <RadioGroupPrimitive.Item
      value={value}
      className={cn(
        "group flex h-10 w-full shrink-0 items-center gap-3 rounded-lg px-3 text-left text-sm outline-none",
        "transition-colors hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring/50",
        "data-[state=checked]:bg-muted disabled:pointer-events-none disabled:opacity-60",
      )}
    >
      <span className="shrink-0 text-muted-foreground group-data-[state=checked]:text-foreground">
        {icon}
      </span>
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {detail && <span className="shrink-0 text-xs text-muted-foreground">{detail}</span>}
      <SelectedCheck />
    </RadioGroupPrimitive.Item>
  );
}

// Fades and scales in when its radio item (the nearest .group) is checked.
function SelectedCheck() {
  return (
    <Check
      aria-hidden
      className={cn(
        "size-4 shrink-0 scale-[0.25] opacity-0 blur-[4px]",
        "transition-[scale,opacity,filter] duration-200 ease-[cubic-bezier(0.2,0,0,1)]",
        "group-data-[state=checked]:scale-100 group-data-[state=checked]:opacity-100 group-data-[state=checked]:blur-none",
      )}
    />
  );
}

function VisibilitySwitch({
  isPrivate,
  onChange,
  disabled,
}: {
  isPrivate: boolean;
  onChange: (isPrivate: boolean) => void;
  disabled: boolean;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Who can see the new channel"
      className="flex h-7 shrink-0 items-center rounded-md bg-muted p-0.5 text-xs"
    >
      {([false, true] as const).map((priv) => (
        <button
          key={String(priv)}
          type="button"
          role="radio"
          aria-checked={isPrivate === priv}
          disabled={disabled}
          onClick={() => onChange(priv)}
          className={cn(
            "h-6 rounded-[4px] px-2 text-muted-foreground outline-none",
            "transition-[color,background-color,box-shadow] hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50",
            "aria-checked:bg-background aria-checked:text-foreground aria-checked:shadow-[0_0_0_1px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.08)]",
          )}
        >
          {priv ? "Private" : "Public"}
        </button>
      ))}
    </div>
  );
}

/**
 * Save-time errors for the default channel, including creating a staged
 * channel: plain-language fixes for the cases the person can act on.
 */
export function DefaultChannelSaveError({
  error,
  platform = "slack",
}: {
  error: unknown;
  platform?: ChannelPlatform;
}) {
  const code = error instanceof ApiError ? error.code : null;
  const detail = error instanceof ApiError ? `${error.code} ${error.message}` : "";
  const where = PLATFORM_LABEL[platform];
  const message =
    code === "name_taken"
      ? "That name is taken, maybe by an archived channel. Pick another name."
      : code === "invalid_name"
        ? "Use lowercase letters, numbers and dashes."
        : code === "missing_scope"
          ? `Boo can't create channels in this workspace yet. Create it in ${where}, then refresh.`
          : detail.includes("restricted_action")
            ? `Your ${where} admin limits who can create channels. Create it in ${where}, then refresh.`
            : code === "not_found"
              ? "That channel no longer exists. Refresh and pick again."
              : code === "not_in_channel" && error instanceof ApiError
                ? // The Boo names the channel, e.g. "Invite Boo to #name first."
                  `${error.message} Then try again.`
                : null;
  if (!message) return <InlineError error={error} />;
  return <p className="text-sm text-destructive">{message}</p>;
}

// Where to find a channel ID on platforms Boo can't list.
const CHANNEL_ID_HINT: Partial<Record<ChannelPlatform, string>> = {
  gchat: "The space ID from the Google Chat URL, e.g. spaces/AAAA1234.",
  teams: "The channel ID from the Teams link, e.g. 19:…@thread.tacv2.",
  discord: "Turn on Developer Mode, then right-click the channel and copy its ID.",
  slack: "At the bottom of the channel's details in Slack.",
};

function ChannelIdEntry({
  containerId,
  platform,
  disabled,
  selected,
  onUse,
}: {
  containerId: string;
  platform: ChannelPlatform;
  disabled: boolean;
  selected: { channelOrSpaceId: string; displayName: string | null } | null;
  onUse: (row: Row) => void;
}) {
  const [id, setId] = useState(selected?.channelOrSpaceId ?? "");
  const lookupId = useDebounced(id.trim(), 400);
  const meta = useQuery({
    queryKey: ["channel-meta", containerId, platform, lookupId],
    queryFn: () => api.containers.channels.meta(containerId, platform, lookupId),
    enabled: lookupId.length > 0,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const current = lookupId.length > 0 && id.trim() === lookupId;
  const found = current && meta.data?.supported === true ? meta.data : undefined;
  const notFound = current && meta.error instanceof ApiError && meta.error.status === 404;
  const isSelected = selected?.channelOrSpaceId === id.trim();

  return (
    <div className="flex flex-col gap-2 p-2">
      <div className="flex gap-2">
        <Input
          placeholder="Channel ID"
          value={id}
          aria-label="Channel ID"
          onChange={(e) => setId(e.target.value)}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0"
          disabled={disabled || id.trim().length === 0 || isSelected || meta.isFetching}
          onClick={() =>
            onUse({
              channelOrSpaceId: id.trim(),
              displayName: found?.displayName ?? null,
              surfaceType: found?.surfaceType ?? "channel",
            })
          }
        >
          {isSelected ? "Selected" : "Use"}
        </Button>
      </div>
      <p
        className={cn(
          "min-h-4 text-xs",
          notFound && !meta.isFetching
            ? "text-amber-600 dark:text-amber-500"
            : "text-muted-foreground",
        )}
      >
        {current && meta.isFetching
          ? "Checking…"
          : found
            ? `Found ${bareChannelName(found.displayName ?? null) ?? lookupId}`
            : notFound
              ? `${PLATFORM_LABEL[platform]} doesn't know this ID.`
              : (CHANNEL_ID_HINT[platform] ?? " ")}
      </p>
    </div>
  );
}
